import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia, 
  Button,
  Divider,
  Chip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import LandingHeader from '../components/LandingHeader';
import Footer from '../components/Footer';

const BlogPage: React.FC = () => {
  const navigate = useNavigate();
  
  const blogPosts = [
    {
      id: 1,
      title: 'AI in Legal Document Analysis: The Future is Here',
      summary: 'How artificial intelligence is transforming the way legal professionals analyze and draft documents.',
      image: 'https://images.unsplash.com/photo-1589829085413-56de8ae18c73?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2940&q=80',
      date: 'May 15, 2023',
      category: 'Technology',
      author: 'Sarah Johnson'
    },
    {
      id: 2,
      title: '5 Ways Contract AI Reduces Legal Risk',
      summary: 'Discover how AI-powered contract analysis can help identify and mitigate potential legal risks before they become problems.',
      image: 'https://images.unsplash.com/photo-1543286386-713bdd548da4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2940&q=80',
      date: 'June 3, 2023',
      category: 'Risk Management',
      author: 'Michael Chen'
    },
    {
      id: 3,
      title: 'The ROI of Implementing Contract AI in Your Legal Department',
      summary: 'A detailed analysis of the return on investment when implementing AI contract solutions in corporate legal departments.',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2940&q=80',
      date: 'June 28, 2023',
      category: 'Business',
      author: 'Emily Rodriguez'
    },
    {
      id: 4,
      title: 'Contract Automation: Best Practices for 2023',
      summary: 'Learn the latest best practices for implementing contract automation in your organization.',
      image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2940&q=80',
      date: 'July 12, 2023',
      category: 'Best Practices',
      author: 'David Wilson'
    },
    {
      id: 5,
      title: 'Legal Tech Trends to Watch in 2023',
      summary: 'Stay ahead of the curve with these emerging trends in legal technology that are shaping the future of the industry.',
      image: 'https://images.unsplash.com/photo-1581091226033-d5c48150dbaa?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2940&q=80',
      date: 'July 30, 2023',
      category: 'Industry Trends',
      author: 'Jennifer Lee'
    },
    {
      id: 6,
      title: 'Case Study: How Fortune 500 Company Saved $2M with Contract AI',
      summary: 'A detailed case study on how a Fortune 500 company implemented Contract AI and achieved significant cost savings.',
      image: 'https://images.unsplash.com/photo-1507679799987-c73779587ccf?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2942&q=80',
      date: 'August 15, 2023',
      category: 'Case Study',
      author: 'Robert Thompson'
    }
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box sx={{ bgcolor: '#1a365d', color: 'white', py: 8, pt: 4 }}>
        {/* Header */}
        <LandingHeader />
        
        <Container maxWidth="lg">
          <Typography variant="h2" component="h1" fontWeight="bold" gutterBottom>
            Contract AI Blog
          </Typography>
          <Typography variant="h6" sx={{ maxWidth: '700px', mb: 4, opacity: 0.9 }}>
            Insights, trends, and best practices in AI-powered contract management and legal document analysis
          </Typography>
        </Container>
      </Box>
      
      {/* Featured Post */}
      <Container maxWidth="lg" sx={{ mt: -4 }}>
        <Card sx={{ 
          borderRadius: 3, 
          overflow: 'hidden', 
          boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
          mb: 6
        }}>
          <Grid container>
            <Grid item xs={12} md={6}>
              <CardMedia
                component="img"
                height="400"
                image={blogPosts[0].image}
                alt={blogPosts[0].title}
                sx={{ height: '100%' }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CardContent sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Chip 
                  label={blogPosts[0].category} 
                  size="small" 
                  sx={{ 
                    bgcolor: '#e3f2fd', 
                    color: '#1976d2',
                    mb: 2
                  }} 
                />
                <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
                  {blogPosts[0].title}
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 2, flexGrow: 1 }}>
                  {blogPosts[0].summary}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    By {blogPosts[0].author} | {blogPosts[0].date}
                  </Typography>
                  <Button 
                    variant="outlined" 
                    color="primary"
                    onClick={() => navigate(`/blog/${blogPosts[0].id}`)}
                  >
                    Read More
                  </Button>
                </Box>
              </CardContent>
            </Grid>
          </Grid>
        </Card>
      </Container>
      
      {/* Blog Posts Grid */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom sx={{ mb: 4 }}>
          Latest Articles
        </Typography>
        
        <Grid container spacing={4}>
          {blogPosts.slice(1).map((post) => (
            <Grid item xs={12} sm={6} md={4} key={post.id}>
              <Card sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                borderRadius: 2,
                transition: '0.3s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                }
              }}>
                <CardMedia
                  component="img"
                  height="200"
                  image={post.image}
                  alt={post.title}
                />
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Chip 
                    label={post.category} 
                    size="small" 
                    sx={{ 
                      bgcolor: '#e3f2fd', 
                      color: '#1976d2',
                      mb: 2
                    }} 
                  />
                  <Typography variant="h5" component="h3" fontWeight="bold" gutterBottom>
                    {post.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {post.summary}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {post.date}
                    </Typography>
                    <Button 
                      variant="text" 
                      color="primary"
                      onClick={() => navigate(`/blog/${post.id}`)}
                    >
                      Read More
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <Button 
            variant="contained" 
            color="primary"
            size="large"
            sx={{ 
              py: 1.5, 
              px: 4,
              borderRadius: 2
            }}
          >
            Load More Articles
          </Button>
        </Box>
      </Container>
      
      {/* Newsletter */}
      <Box sx={{ bgcolor: '#f8faff', py: 8 }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', maxWidth: '600px', mx: 'auto' }}>
            <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
              Subscribe to Our Newsletter
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 4 }}>
              Get the latest insights on contract management, legal tech, and AI delivered straight to your inbox.
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' }, 
              gap: 2,
              maxWidth: '500px',
              mx: 'auto'
            }}>
              <Box 
                component="input" 
                type="email" 
                placeholder="Your email address"
                sx={{ 
                  flex: 1,
                  py: 1.5,
                  px: 2,
                  borderRadius: 2,
                  border: '1px solid #ddd',
                  fontSize: '1rem',
                  width: '100%',
                  boxSizing: 'border-box'
                }}
              />
              <Button 
                variant="contained" 
                color="primary"
                sx={{ 
                  py: 1.5, 
                  px: 3,
                  borderRadius: 2,
                  whiteSpace: 'nowrap'
                }}
              >
                Subscribe Now
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
      
      <Footer />
    </Box>
  );
};

export default BlogPage; 